/**
 * Sprint 12+L2 (15.07.2026) — Pionier-Onboarding.
 *
 * Zwei-Stufen-Test explizit für Pioniere. Stufe 1: 5 A/B/C/D-Fragen aus
 * QUICK_CHECK_V4_QUESTIONS mit Pionier-Context-Wrapper. Nach Ergebnis +
 * statischer Deutung entscheidet der User optional Stufe 2 (20 Likert-
 * Fragen aus dem Backend /api/quiz/detail/questions).
 *
 * Backend-Endpoints:
 *   POST /api/pionier/onboarding/lite      → { fokustyp, deutung, next_step_prompt }
 *   POST /api/pionier/onboarding/detail    → { fokustyp, deutung, changed_from_lite }
 *   POST /api/pionier/onboarding/skip-detail
 *   GET  /api/pionier/onboarding/status
 *
 * Free-User-Frontend (/pionier-quick-check) bleibt unberührt.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { QUICK_CHECK_V4_QUESTIONS } from "@/lib/quickCheckV4";
import { api } from "@/lib/api";

const STAGE_LITE = "lite";
const STAGE_LITE_RESULT = "lite_result";
const STAGE_DETAIL = "detail";
const STAGE_DETAIL_RESULT = "detail_result";
const STAGE_LOADING = "loading";
const STAGE_ERROR = "error";

const PIONIER_ACCENT = "#4563BB"; // dezenter Brand-Akzent für Pionier-Context

function PionierHeader() {
  return (
    <header
      className="w-full max-w-2xl mx-auto pt-8 pb-6 flex items-center gap-3"
      data-testid="pionier-onboarding-header"
    >
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-widest"
        style={{ backgroundColor: `${PIONIER_ACCENT}15`, color: PIONIER_ACCENT }}
      >
        <Sparkles className="w-3.5 h-3.5" />
        Pionier
      </span>
      <span className="text-sm text-slate-500">
        Du bist eine der ersten Personen, die das erlebt.
      </span>
    </header>
  );
}

function ProgressBar({ current, total, label }) {
  const pct = Math.round((current / total) * 100);
  return (
    <div className="mb-6" data-testid="pionier-onboarding-progress">
      <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
        <span>{label}</span>
        <span>
          {current} von {total} · {pct}%
        </span>
      </div>
      <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
        <div
          className="h-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: PIONIER_ACCENT }}
        />
      </div>
    </div>
  );
}

function LiteStage({ onSubmit, submitting }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const total = QUICK_CHECK_V4_QUESTIONS.length;
  const q = QUICK_CHECK_V4_QUESTIONS[step];

  const choose = (letter) => {
    const next = { ...answers, [q.id]: letter };
    setAnswers(next);
    if (step < total - 1) {
      setTimeout(() => setStep(step + 1), 200);
    } else {
      const ordered = QUICK_CHECK_V4_QUESTIONS.map((qq) => next[qq.id]);
      onSubmit(ordered);
    }
  };

  return (
    <div data-testid="pionier-onboarding-lite-stage">
      <ProgressBar current={step + 1} total={total} label="Stufe 1 · Grundmuster (5 Fragen)" />
      <p className="text-xs uppercase tracking-widest mb-3" style={{ color: PIONIER_ACCENT }}>
        Als Pionier testest du mit uns die Grundlagen — deine Antwort hilft uns, die App zu verbessern.
      </p>
      <h2 className="text-xl sm:text-2xl font-bold mb-2 text-slate-900">
        {q.situation}
      </h2>
      <p className="text-base text-slate-700 mb-6">{q.frage}</p>
      <div className="space-y-3">
        {q.options.map((opt, idx) => {
          // Sprint M5.42 Block B Hotfix (31.07.2026): Anzeige-Buchstabe
          // immer A, B, C, D chronologisch — opt.letter bleibt intern fuer
          // Backend-Mapping.
          const displayLetter = String.fromCharCode(65 + idx);
          return (
            <button
              key={opt.letter}
              type="button"
              disabled={submitting}
              onClick={() => choose(opt.letter)}
              className="w-full text-left border rounded-lg px-4 py-4 hover:bg-slate-50 transition-colors disabled:opacity-50 flex gap-3"
              style={{ borderColor: "#d4d4d0" }}
              data-testid={`pionier-lite-option-${opt.letter}`}
              data-display-letter={displayLetter}
            >
              <span
                className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm"
                style={{ backgroundColor: `${PIONIER_ACCENT}15`, color: PIONIER_ACCENT }}
              >
                {displayLetter}
              </span>
              <span className="text-slate-800 leading-relaxed">{opt.text}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DeutungBlock({ deutung, testId }) {
  return (
    <div className="text-left" data-testid={testId}>
      <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-2">
        {deutung.titel}
      </h2>
      <p className="text-lg text-slate-700 mb-4">{deutung.kurz}</p>
      <p className="text-base text-slate-700 leading-relaxed mb-6">{deutung.text}</p>

      {Array.isArray(deutung.beispielsituationen) && deutung.beispielsituationen.length > 0 && (
        <div className="mb-6" data-testid={`${testId}-beispiele`}>
          <div className="text-xs uppercase tracking-widest mb-3" style={{ color: PIONIER_ACCENT }}>
            Erkennst du dich wieder?
          </div>
          <ul className="space-y-2 list-none">
            {deutung.beispielsituationen.map((s, i) => (
              <li key={i} className="text-base text-slate-700 leading-relaxed pl-4 border-l-2" style={{ borderColor: `${PIONIER_ACCENT}40` }}>
                {s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {Array.isArray(deutung.loesungsfragen) && deutung.loesungsfragen.length > 0 && (
        <div className="mb-6" data-testid={`${testId}-loesungsfragen`}>
          <div className="text-xs uppercase tracking-widest mb-3" style={{ color: PIONIER_ACCENT }}>
            Fragen, mit denen du weiterkommst
          </div>
          <ul className="space-y-2 list-none">
            {deutung.loesungsfragen.map((f, i) => (
              <li key={i} className="text-base text-slate-800 leading-relaxed font-medium">
                → {f}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div
        className="rounded-lg px-4 py-3 text-sm mb-4"
        style={{ backgroundColor: `${PIONIER_ACCENT}10`, color: "#1a1a1a" }}
      >
        <div className="text-xs uppercase tracking-widest mb-1" style={{ color: PIONIER_ACCENT }}>
          Deine Handlung diese Woche
        </div>
        {deutung.handlung}
      </div>

      {deutung.app_verweis && (
        <p className="text-sm text-slate-600 italic leading-relaxed" data-testid={`${testId}-app-verweis`}>
          {deutung.app_verweis}
        </p>
      )}
    </div>
  );
}

const FOKUSTYP_ICONS = { eule: "🦉", biene: "🐝", katze: "🐈", hund: "🐕" };

function Steckbrief({ result }) {
  const scoreMap = result.score_map || {};
  const total = Object.values(scoreMap).reduce((a, b) => a + b, 0) || 1;
  const firstName = result.first_name || "";
  const fokustyp = result.fokustyp;
  return (
    <div
      className="rounded-2xl border p-6 mb-8"
      style={{ borderColor: "#d4d4d0", backgroundColor: "#ffffff" }}
      data-testid="pionier-steckbrief"
    >
      <div className="flex items-start gap-4">
        <div
          className="flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center text-3xl"
          style={{ backgroundColor: `${PIONIER_ACCENT}15` }}
        >
          {FOKUSTYP_ICONS[fokustyp] || "✦"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest mb-1" style={{ color: PIONIER_ACCENT }}>
            Dein Steckbrief
          </div>
          <div className="text-lg font-bold text-slate-900" data-testid="steckbrief-name">
            {firstName ? `${firstName} — ` : ""}
            <span className="capitalize">{fokustyp}</span>
          </div>
          <div className="mt-4 space-y-1.5" data-testid="steckbrief-score-verteilung">
            {["katze", "eule", "biene", "hund"].map((ft) => {
              const pct = Math.round((scoreMap[ft] || 0) * 100 / total);
              return (
                <div key={ft} className="flex items-center gap-3 text-sm">
                  <span className="w-14 text-slate-600 capitalize">{ft}</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: ft === fokustyp ? PIONIER_ACCENT : "#cbd5e1",
                      }}
                    />
                  </div>
                  <span className="w-10 text-right text-slate-500 tabular-nums">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function HelpValueToggle({ onDecide, decided, decision, submitting }) {
  return (
    <div
      className="rounded-lg border p-5 mb-8"
      style={{ borderColor: `${PIONIER_ACCENT}40`, backgroundColor: `${PIONIER_ACCENT}05` }}
      data-testid="pionier-help-value-toggle"
    >
      <div className="text-xs uppercase tracking-widest mb-2" style={{ color: PIONIER_ACCENT }}>
        Willst du echter Pionier werden?
      </div>
      <p className="text-sm text-slate-700 leading-relaxed mb-4">
        Als Pionier bekommst du zusätzlich zum frühen App-Zugang:
        <br />• Wöchentlicher Pionier-Q&amp;A-Call mit Andreas (persönlich)
        <br />• Permanente Feedback-Schleife (deine Wünsche formen die App)
        <br />• Mitgestaltung, welche Übersetzer als Erstes gebaut werden
      </p>
      {decided ? (
        <div className="text-sm font-semibold" data-testid="pionier-help-value-decided">
          {decision
            ? "✓ Du bist auf der Pionier-Liste. Wir melden uns."
            : "Alles gut — nur der Test, keine Extras."}
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={() => onDecide(true)}
            disabled={submitting}
            className="flex-1 h-11 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-70"
            style={{ backgroundColor: PIONIER_ACCENT }}
            data-testid="pionier-help-value-yes"
          >
            Ja, ich will Pionier werden
          </button>
          <button
            type="button"
            onClick={() => onDecide(false)}
            disabled={submitting}
            className="flex-1 h-11 rounded-lg text-sm font-semibold border transition-colors disabled:opacity-70"
            style={{ borderColor: "#d4d4d0", color: "#1a1a1a" }}
            data-testid="pionier-help-value-no"
          >
            Nein, erstmal nicht
          </button>
        </div>
      )}
    </div>
  );
}

function LiteResultStage({ result, onWantDetail, onSkip, onHelpValue, helpValueDecided, helpValueDecision, submitting }) {
  return (
    <div data-testid="pionier-onboarding-lite-result">
      <div className="flex items-center gap-2 mb-6 text-slate-500">
        <CheckCircle2 className="w-5 h-5" style={{ color: "#15803d" }} />
        <span className="text-sm">Stufe 1 abgeschlossen</span>
      </div>
      <Steckbrief result={result} />
      <DeutungBlock deutung={result.deutung} testId="pionier-lite-deutung" />
      <div
        className="mt-10 border-t pt-8"
        data-testid="pionier-onboarding-stage2-prompt"
      >
        <HelpValueToggle
          onDecide={onHelpValue}
          decided={helpValueDecided}
          decision={helpValueDecision}
          submitting={submitting}
        />
        <p className="text-base text-slate-800 mb-6">{result.next_step_prompt?.question}</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={onWantDetail}
            disabled={submitting}
            className="flex-1 h-12 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-70 inline-flex items-center justify-center gap-2"
            style={{ backgroundColor: PIONIER_ACCENT }}
            data-testid="pionier-stage2-yes-btn"
          >
            {result.next_step_prompt?.yes_label || "Ja, präzisere Deutung"}
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onSkip}
            disabled={submitting}
            className="flex-1 h-12 rounded-lg text-sm font-semibold border transition-colors disabled:opacity-70"
            style={{ borderColor: "#d4d4d0", color: "#1a1a1a" }}
            data-testid="pionier-stage2-no-btn"
          >
            {result.next_step_prompt?.no_label || "Nein, erstmal so"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailStage({ questions, answerOptions, onSubmit, submitting }) {
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState({});
  const total = questions.length;
  const q = questions[step];

  const choose = (value) => {
    const next = { ...scores, [q.id]: value };
    setScores(next);
    if (step < total - 1) {
      setTimeout(() => setStep(step + 1), 150);
    } else {
      const payload = questions.map((qq) => ({ question_id: qq.id, score: next[qq.id] }));
      onSubmit(payload);
    }
  };

  return (
    <div data-testid="pionier-onboarding-detail-stage">
      <ProgressBar current={step + 1} total={total} label="Stufe 2 · Präzisere Deutung (20 Fragen)" />
      <p className="text-xs uppercase tracking-widest mb-3" style={{ color: PIONIER_ACCENT }}>
        Als Pionier hilfst du uns, das feiner zu machen.
      </p>
      <h2
        className="text-xl sm:text-2xl font-semibold mb-6 text-slate-900 leading-snug"
        data-testid="pionier-detail-statement"
      >
        „{q.statement}&rdquo;
      </h2>
      <div className="grid grid-cols-1 gap-2">
        {answerOptions.map((opt) => (
          <button
            key={opt.value}
            type="button"
            disabled={submitting}
            onClick={() => choose(opt.value)}
            className="text-left border rounded-lg px-4 py-3 hover:bg-slate-50 transition-colors disabled:opacity-50 flex items-center gap-3"
            style={{ borderColor: "#d4d4d0" }}
            data-testid={`pionier-detail-option-${opt.value}`}
          >
            <span className="text-xl">{opt.smiley}</span>
            <span className="text-slate-800">{opt.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function DetailResultStage({ result, onDone }) {
  return (
    <div data-testid="pionier-onboarding-detail-result">
      <div className="flex items-center gap-2 mb-6 text-slate-500">
        <CheckCircle2 className="w-5 h-5" style={{ color: "#15803d" }} />
        <span className="text-sm">
          Stufe 2 abgeschlossen{" "}
          {result.changed_from_lite && (
            <em className="ml-1" style={{ color: PIONIER_ACCENT }}>
              — deine Detail-Antworten haben ein anderes Muster ergeben als der Kurz-Test.
            </em>
          )}
        </span>
      </div>
      <DeutungBlock deutung={result.deutung} testId="pionier-detail-deutung" />
      <button
        type="button"
        onClick={onDone}
        className="mt-10 w-full h-12 rounded-lg text-sm font-semibold text-white transition-colors inline-flex items-center justify-center gap-2"
        style={{ backgroundColor: "#1a1a1a" }}
        data-testid="pionier-detail-done-btn"
      >
        Weiter zum PocketCoach
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function PionierOnboarding() {
  const navigate = useNavigate();
  const [stage, setStage] = useState(STAGE_LOADING);
  const [errorMsg, setErrorMsg] = useState("");
  const [liteResult, setLiteResult] = useState(null);
  const [detailResult, setDetailResult] = useState(null);
  const [detailQuestions, setDetailQuestions] = useState([]);
  const [detailAnswerOptions, setDetailAnswerOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [helpValueDecided, setHelpValueDecided] = useState(false);
  const [helpValueDecision, setHelpValueDecision] = useState(null);

  // Initial: Status abfragen — falls User schon lite gemacht hat, springen wir dort weiter
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/pionier/onboarding/status");
        if (cancelled) return;
        const status = data?.onboarding?.status;
        if (status === "detail_completed" || status === "detail_skipped") {
          navigate("/dashboard/pionier", { replace: true });
          return;
        }
        setStage(STAGE_LITE);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          navigate("/login", { replace: true });
          return;
        }
        if (err?.response?.status === 403) {
          setErrorMsg("Diese Seite ist nur für Pioniere. Bitte über den Bestätigungs-Link einloggen.");
          setStage(STAGE_ERROR);
          return;
        }
        setErrorMsg("Kurze Störung. Bitte in einem Moment neu laden.");
        setStage(STAGE_ERROR);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const submitLite = async (answers) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/pionier/onboarding/lite", { answers });
      setLiteResult(data);
      setStage(STAGE_LITE_RESULT);
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || "Speichern fehlgeschlagen. Bitte erneut versuchen.");
      setStage(STAGE_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const startDetail = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.get("/quiz/detail/questions");
      setDetailQuestions(data?.questions || []);
      setDetailAnswerOptions(data?.answer_options || []);
      setStage(STAGE_DETAIL);
    } catch (err) {
      setErrorMsg("Detail-Fragen konnten nicht geladen werden.");
      setStage(STAGE_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const skipDetail = async () => {
    setSubmitting(true);
    try {
      await api.post("/pionier/onboarding/skip-detail");
      navigate("/dashboard/pionier", { replace: true });
    } catch (err) {
      setErrorMsg("Konnte nicht überspringen. Bitte erneut versuchen.");
      setStage(STAGE_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const submitDetail = async (answers) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/pionier/onboarding/detail", { answers });
      setDetailResult(data);
      setStage(STAGE_DETAIL_RESULT);
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || "Speichern fehlgeschlagen.");
      setStage(STAGE_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const done = () => navigate("/dashboard/pionier", { replace: true });

  const decideHelpValue = async (optIn) => {
    setSubmitting(true);
    try {
      await api.post("/pionier/help-value", { opt_in: optIn });
      setHelpValueDecided(true);
      setHelpValueDecision(optIn);
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || "Konnte deine Entscheidung nicht speichern.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: "#FAFAF7", color: "#1a1a1a" }}
      data-testid="pionier-onboarding-page"
    >
      <PionierHeader />
      <main className="flex-1 px-6 pb-16">
        <div className="w-full max-w-2xl mx-auto">
          {stage === STAGE_LOADING && (
            <div className="text-center py-16" data-testid="pionier-onboarding-loading">
              <Loader2
                className="w-8 h-8 mx-auto animate-spin"
                style={{ color: PIONIER_ACCENT }}
              />
              <p className="mt-4 text-sm text-slate-500">Wird geladen …</p>
            </div>
          )}
          {stage === STAGE_ERROR && (
            <div className="text-center py-16" data-testid="pionier-onboarding-error">
              <p className="text-base text-slate-800 mb-4">{errorMsg}</p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="text-sm underline text-slate-600"
              >
                Neu laden
              </button>
            </div>
          )}
          {stage === STAGE_LITE && (
            <LiteStage onSubmit={submitLite} submitting={submitting} />
          )}
          {stage === STAGE_LITE_RESULT && liteResult && (
            <LiteResultStage
              result={liteResult}
              onWantDetail={startDetail}
              onSkip={skipDetail}
              onHelpValue={decideHelpValue}
              helpValueDecided={helpValueDecided}
              helpValueDecision={helpValueDecision}
              submitting={submitting}
            />
          )}
          {stage === STAGE_DETAIL && detailQuestions.length > 0 && (
            <DetailStage
              questions={detailQuestions}
              answerOptions={detailAnswerOptions}
              onSubmit={submitDetail}
              submitting={submitting}
            />
          )}
          {stage === STAGE_DETAIL_RESULT && detailResult && (
            <DetailResultStage result={detailResult} onDone={done} />
          )}
        </div>
      </main>
      <footer className="py-6 text-center text-xs" style={{ color: "#94a3b8" }}>
        Flex4Success · Pionier-Onboarding
      </footer>
    </div>
  );
}
